import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../services/api';
import { setStoredCurrencyCode } from '../utils/currency';
import { setStoredLanguageCode } from '../utils/i18n';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('flexigym_token');
    const storedUser = localStorage.getItem('flexigym_user');

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        
        // Token expiration check disabled - tokens remain valid until logout
        // Validate token claims match stored user data
        const tokenClaims = getTokenClaims(storedToken);
        if (!validateTokenUserMatch(tokenClaims, parsedUser)) {
          console.warn('Token claims do not match stored user data, clearing session');
          localStorage.removeItem('flexigym_user');
          localStorage.removeItem('flexigym_token');
          return;
        }
        
        setToken(storedToken);
        setUser(parsedUser);

        // Load gym settings for gym owners
        if (parsedUser.role === 'gym_owner' && parsedUser.gym_id) {
          loadGymSettings(storedToken, parsedUser.gym_id);
        }
      } catch (e) {
        console.error('Error parsing stored user data:', e);
        localStorage.removeItem('flexigym_user');
        localStorage.removeItem('flexigym_token');
      }
    }
    setLoading(false);
  }, []);

  const loadGymSettings = async (authToken, gymId) => {
    try {
      const response = await api.get('/api/gym/profile', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (response.data.gym) {
        const gym = response.data.gym;
        // Initialize currency and language from gym settings
        if (gym.currency) {
          setStoredCurrencyCode(gym.currency, gymId);
        }
        if (gym.language) {
          setStoredLanguageCode(gym.language, gymId);
        }
      }
    } catch (error) {
      console.error('Failed to load gym settings:', error);
      // Don't fail login if settings fail to load
    }
  };

  const login = async (newToken, newUser) => {
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('flexigym_token', newToken);
    localStorage.setItem('flexigym_user', JSON.stringify(newUser));

    // Load gym settings for gym owners
    if (newUser.role === 'gym_owner' && newUser.gym_id) {
      await loadGymSettings(newToken, newUser.gym_id);
    }
  };

  const logout = async () => {
    const gymId = user?.gym_id;
    
    // Call backend logout API to set gym status to CLOSED for gym owners
    try {
      await api.post('/api/auth/logout');
    } catch (err) {
      console.error('Logout API call failed:', err);
      // Continue with local logout even if API call fails
    }
    
    setToken(null);
    setUser(null);
    localStorage.removeItem('flexigym_token');
    localStorage.removeItem('flexigym_user');
    
    // Clear gym-specific settings if gym_id exists
    if (gymId) {
      localStorage.removeItem(`flexigym_currency_${gymId}`);
      localStorage.removeItem(`flexigym_language_${gymId}`);
    }
  };

  // Enhanced authentication checks
  const isAuthenticated = !!token && !!user;
  
  // Role-based checks
  const isSuperAdmin = user?.role === 'super_admin';
  const isGymOwner = user?.role === 'gym_owner';
  const isMember = user?.role === 'member';
  
  // Permission checks
  const hasPermission = (permission) => {
    if (!user) return false;
    
    const permissions = getRolePermissions(user.role);
    return permissions[permission] || false;
  };
  
  // Check if user can access admin features
  const canAccessAdminFeatures = isSuperAdmin;
  
  // Check if user can access gym management
  const canAccessGymManagement = isSuperAdmin || isGymOwner;
  
  // Get user's gym information
  const getUserGym = () => {
    if (!user) return null;
    
    return {
      id: user.gym_id,
      name: user.gym_name,
      address: user.gym_address,
      phone: user.gym_phone
    };
  };
  
  // Check if user belongs to a specific gym
  const belongsToGym = (gymId) => {
    if (isSuperAdmin) return true; // Super admins can access all gyms
    return user?.gym_id === gymId;
  };
  
  // Get user display information
  const getUserDisplayInfo = () => {
    if (!user) return null;
    
    return {
      name: user.name,
      email: user.email,
      role: user.role,
      roleDisplay: getRoleDisplayName(user.role),
      initials: getInitials(user.name),
      gym: getUserGym(),
      isSuperAdmin,
      isGymOwner,
      isMember
    };
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      logout, 
      isAuthenticated,
      // Role checks
      isSuperAdmin,
      isGymOwner,
      isMember,
      // Permission checks
      hasPermission,
      canAccessAdminFeatures,
      canAccessGymManagement,
      // Utility functions
      getUserGym,
      belongsToGym,
      getUserDisplayInfo
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper functions
const getTokenClaims = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

const validateTokenUserMatch = (tokenClaims, userData) => {
  if (!tokenClaims || !userData) return false;
  
  // Validate essential fields match between token and stored user data
  const roleMatches = tokenClaims.role === userData.role;
  
  // Skip gym_id validation for super admin
  if (tokenClaims.role === 'super_admin' || userData.role === 'super_admin') {
    return roleMatches;
  }
  
  // For other roles, compare gym_id with String coercion to avoid type mismatches
  const gymIdMatches = String(tokenClaims.gym_id) === String(userData.gym_id);
  
  return roleMatches && gymIdMatches;
};

const getRolePermissions = (role) => {
  const permissions = {
    'super_admin': {
      'canManageAllGyms': true,
      'canManageAllUsers': true,
      'canViewAllData': true,
      'canModifySubscriptions': true,
      'canViewAnalytics': true,
      'canManageSystemSettings': true,
      'canAccessAdminPanel': true
    },
    'gym_owner': {
      'canManageAllGyms': false,
      'canManageAllUsers': false,
      'canViewAllData': false,
      'canModifySubscriptions': false,
      'canViewAnalytics': true,
      'canManageSystemSettings': false,
      'canAccessAdminPanel': false,
      'canManageOwnGym': true,
      'canManageOwnMembers': true
    },
    'member': {
      'canManageAllGyms': false,
      'canManageAllUsers': false,
      'canViewAllData': false,
      'canModifySubscriptions': false,
      'canViewAnalytics': false,
      'canManageSystemSettings': false,
      'canAccessAdminPanel': false,
      'canManageOwnGym': false,
      'canManageOwnMembers': false,
      'canViewOwnData': true
    }
  };
  
  return permissions[role] || permissions['member'];
};

const getRoleDisplayName = (role) => {
  const roleNames = {
    'super_admin': 'Super Administrator',
    'gym_owner': 'Gym Owner',
    'member': 'Member'
  };
  
  return roleNames[role] || 'User';
};

const getInitials = (name) => {
  if (!name) return 'U';
  
  return name
    .split(' ')
    .map(part => part.charAt(0).toUpperCase())
    .join('')
    .substring(0, 2);
};
